/**
 * ChallengesTableV2 — Challenges list, ported from the prototype's
 * `.screen-challenges` (cms-redesign-mockup.html ~line 8043+, styling
 * for `.challenge-list-wrap` + `.ch-cover` + `.ch-stat` 2219–2278).
 *
 * Layout:
 *   • Status SegmentedControl (All / Active / Inactive with counts).
 *   • LibTable with columns:
 *       Order    — drag handle + 2-digit zero-padded sort number
 *       Status   — StatusEdge thin colored bar
 *       Cover    — 60×40 landscape thumb (challenge_image)
 *       Title    — name + optional "New" coral state-badge
 *       Days     — `.ch-stat` (big condensed number + uppercase label)
 *       Sessions — same
 *       Joins    — same, but number tinted teal-green-d
 *       Actions  — hover-revealed Edit link to /challenges/edit/{id}
 *
 * Drag-to-reorder via @dnd-kit, mirroring the Categories pattern.
 * Drop dispatches the existing `sortChallenges` action (POST
 * /challenges/sort with a JSON-stringified array of ids in the new
 * order) — same payload v1 already uses.
 *
 * Data source: GET /challenges (legacy collection envelope). After
 * #561 the response includes `challenge_image`, `authors[]`,
 * `challenge_is_free`, and `challenge_is_new`, so no API work was
 * needed here.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { createSelector } from 'reselect';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  fetchAllChallenges,
  sortChallenges,
} from '../../../actions/challengeActions';
import {
  LibTable,
  SegmentedControl,
  IconButton,
  StatusEdge,
  cn,
} from '../../redesign';

/* ─── Icons ────────────────────────────────────────────────────────── */

const DragHandleIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="9"  cy="6"  r="0.5" />
    <circle cx="9"  cy="12" r="0.5" />
    <circle cx="9"  cy="18" r="0.5" />
    <circle cx="15" cy="6"  r="0.5" />
    <circle cx="15" cy="12" r="0.5" />
    <circle cx="15" cy="18" r="0.5" />
  </svg>
);

const EditIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);

/* ─── Helpers ──────────────────────────────────────────────────────── */

// `01`, `02`, …, `28` — JetBrains Mono numeric badge. Padded to two
// digits so the column stays aligned visually even when the count
// crosses 10.
function pad2(n) {
  const s = String(n);
  return s.length < 2 ? `0${s}` : s;
}

const fmtInt = new Intl.NumberFormat('en-US');

const selectChallenges = createSelector(
  (s) => s.challenges.challenges,
  (m) => (m ? Object.values(m) : null),
);

/* ─── ChStat — two-line stat cell (num + uppercase label) ──────────── */
/*
 *  Mirrors `.ch-stat` (prototype 2259–2274). Used for Days, Sessions,
 *  and Joins. The Joins variant gets a teal-green-d tint on the
 *  number to emphasise it (it's the social-proof metric).
 */
function ChStat({ num, label, tone, muted }) {
  const fontFamily = 'var(--font-condensed)';
  return (
    <span
      className="inline-flex flex-col"
      style={{ lineHeight: 1.15 }}
    >
      <span
        style={{
          fontFamily,
          fontWeight: 700,
          fontSize: 16,
          color: tone === 'joins'
            ? '#5C7E72' /* teal-green-d */
            : muted
              ? '#9CA1A8' /* text-muted */
              : '#2C3E50', /* navy */
          letterSpacing: '-0.005em',
        }}
      >
        {num}
      </span>
      <span
        className="text-text-muted"
        style={{
          fontFamily,
          fontSize: 10.5,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontWeight: 700,
        }}
      >
        {label}
      </span>
    </span>
  );
}

/* ─── "New" state badge ─────────────────────────────────────────────── */
function NewBadge() {
  return (
    <span
      className="inline-flex items-center font-condensed font-extrabold uppercase text-white rounded-pill shrink-0 bg-coral"
      style={{
        fontSize: 9.5,
        letterSpacing: '0.08em',
        padding: '2px 7px',
      }}
    >
      New
    </span>
  );
}

/* ─── "Inactive" state badge ────────────────────────────────────────────
 * Explicit red pill so inactive challenges are obvious at a glance in the
 * "All" view — the cream row tint alone wasn't clear enough (Kelly, Monday
 * 12171352875; the old CMS used a red "Inactive" label). */
function InactiveBadge() {
  return (
    <span
      className="inline-flex items-center font-condensed font-extrabold uppercase text-white rounded-pill shrink-0 bg-danger"
      style={{
        fontSize: 9.5,
        letterSpacing: '0.08em',
        padding: '2px 7px',
      }}
    >
      Inactive
    </span>
  );
}

/* ─── Sortable row ─────────────────────────────────────────────────── */

function SortableChallengeRow({ challenge, position }) {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: challenge.challenge_id });

  const active = Number(challenge.challenge_is_active) === 1;
  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    zIndex: isDragging ? 5 : undefined,
  };

  return (
    <LibTable.Row inactive={!active} ref={setNodeRef} style={dragStyle} {...attributes}>
      {/* Order cell: drag handle + 2-digit sort number (prototype
       *  2225–2251). `listeners` only here so the rest of the row
       *  stays clickable / hoverable. */}
      <td
        className="border-b border-line group-data-[inactive=true]:bg-[var(--color-row-inactive)] group-data-[inactive=true]:group-hover:bg-[var(--color-row-inactive-hover)]"
        style={{ padding: '10px 8px 10px 14px' }}
      >
        <div className="flex items-center" style={{ gap: 6 }}>
          <span
            {...listeners}
            aria-label="Drag to reorder"
            tabIndex={0}
            className={cn(
              'inline-grid place-items-center text-text-muted cursor-grab',
              'opacity-[0.35] group-hover:opacity-100',
              'hover:bg-cream hover:text-navy group-data-[inactive=true]:hover:bg-[var(--color-row-inactive-hover)] active:cursor-grabbing',
              'focus:outline-none focus-visible:bg-cream group-data-[inactive=true]:focus-visible:bg-[var(--color-row-inactive-hover)] focus-visible:text-navy',
            )}
            style={{
              width: 22,
              height: 22,
              borderRadius: 4,
              transition: 'all 140ms ease',
              touchAction: 'none',
            }}
          >
            <span style={{ width: 13, height: 13, display: 'inline-flex' }}>
              {DragHandleIcon}
            </span>
          </span>
          <span
            className="text-text-muted font-mono"
            style={{ fontSize: 11.5, fontWeight: 600, minWidth: 16, textAlign: 'right' }}
          >
            {pad2(position)}
          </span>
        </div>
      </td>

      {/* Status edge — thin colored bar (StatusEdge primitive). */}
      <td
        className="border-b border-line group-data-[inactive=true]:bg-[var(--color-row-inactive)] group-data-[inactive=true]:group-hover:bg-[var(--color-row-inactive-hover)]"
        style={{ padding: 0, width: 22, position: 'relative' }}
      >
        <StatusEdge active={active} />
      </td>

      {/* Cover — 60×40 landscape thumb. */}
      <LibTable.Td>
        <div
          className="bg-cream border border-sand"
          style={{
            width: 60,
            height: 40,
            borderRadius: 4,
            backgroundImage: challenge.challenge_image
              ? `url('${challenge.challenge_image}')`
              : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      </LibTable.Td>

      {/* Title + optional "New" badge. Truncate so long names don't
       *  push the layout out. */}
      <LibTable.Td>
        <div className="flex items-center min-w-0" style={{ gap: 6 }}>
          <Link
            to={`/challenges/edit/${challenge.challenge_id}`}
            className={cn(
              'font-semibold no-underline text-navy hover:text-teal-green-dark',
              'block truncate min-w-0',
              !active && 'italic text-text-light',
            )}
            style={{ fontSize: 13.5 }}
            title={challenge.challenge_title || 'Untitled'}
          >
            {challenge.challenge_title || 'Untitled'}
          </Link>
          {!active && <InactiveBadge />}
          {challenge.challenge_is_new && <NewBadge />}
        </div>
      </LibTable.Td>

      {/* Days / Sessions / Joins — `.ch-stat` cluster. */}
      <LibTable.Td>
        <ChStat num={Number(challenge.total_days ?? 0)} label="days" />
      </LibTable.Td>
      <LibTable.Td>
        <ChStat num={Number(challenge.total_sessions ?? 0)} label="sessions" />
      </LibTable.Td>
      <LibTable.Td>
        <ChStat
          num={fmtInt.format(Number(challenge.total_joins ?? 0))}
          label="joins"
          tone="joins"
        />
      </LibTable.Td>

      {/* Edit action — hover-revealed. */}
      <LibTable.Td style={{ textAlign: 'right' }}>
        <div className="inline-flex opacity-0 transition-opacity duration-150 group-hover:opacity-100">
          <Link
            to={`/challenges/edit/${challenge.challenge_id}`}
            aria-label={`Edit ${challenge.challenge_title}`}
          >
            <IconButton size="sm" aria-label="Edit">{EditIconSvg}</IconButton>
          </Link>
        </div>
      </LibTable.Td>
    </LibTable.Row>
  );
}

/* ─── Main ─────────────────────────────────────────────────────────── */

const STATUS_FILTERS = ['all', 'active', 'inactive'];

export default function ChallengesTableV2() {
  const dispatch = useDispatch();
  const challenges = useSelector(selectChallenges);

  const [localList, setLocalList] = useState([]);
  const [status, setStatus] = useState('all');

  useEffect(() => {
    dispatch(fetchAllChallenges());
  }, [dispatch]);

  // Re-sync local list whenever the server list updates (mirrors the
  // CategoriesTableV2 pattern — server is the source of truth on
  // refetch, local mutations on drop).
  useEffect(() => {
    if (!challenges) return;
    const sorted = [...challenges].sort(
      (a, b) => Number(a.challenge_sort_order ?? 0) - Number(b.challenge_sort_order ?? 0),
    );
    setLocalList(sorted);
  }, [challenges]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = localList.findIndex((c) => c.challenge_id === active.id);
      const newIndex = localList.findIndex((c) => c.challenge_id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;

      const next = arrayMove(localList, oldIndex, newIndex);
      setLocalList(next);

      // v1 payload shape: JSON-stringified array of challenge_ids in
      // the new order. The action POSTs PATCH /challenges/sort.
      dispatch(sortChallenges(JSON.stringify(next.map((c) => c.challenge_id))));
    },
    [localList, dispatch],
  );

  // Status filter — client-side, dataset is small (~28 items).
  const visible = useMemo(() => {
    if (status === 'all') return localList;
    return localList.filter((c) =>
      status === 'active'
        ? Number(c.challenge_is_active) === 1
        : Number(c.challenge_is_active) !== 1,
    );
  }, [localList, status]);

  const counts = useMemo(() => {
    return localList.reduce(
      (acc, c) => {
        acc.all += 1;
        if (Number(c.challenge_is_active) === 1) acc.active += 1;
        else acc.inactive += 1;
        return acc;
      },
      { all: 0, active: 0, inactive: 0 },
    );
  }, [localList]);

  const ids = useMemo(() => visible.map((c) => c.challenge_id), [visible]);

  if (!challenges) {
    return (
      <div
        className="flex items-center justify-center text-text-muted"
        style={{ height: 256 }}
      >
        Loading challenges…
      </div>
    );
  }

  return (
    <div>
      {/* Toolbar — status filter only (challenges don't have a view
       *  toggle; the table is the canonical view). */}
      <div
        className="flex items-center"
        style={{ padding: '0 28px 16px', gap: 12 }}
      >
        <SegmentedControl
          value={status}
          onChange={setStatus}
          options={STATUS_FILTERS.map((v) => ({
            value: v,
            label: v === 'all' ? 'All' : v === 'active' ? 'Active' : 'Inactive',
            count: counts[v],
          }))}
        />
      </div>

      {visible.length === 0 ? (
        <div
          className="bg-surface border border-sand text-text-muted italic text-center"
          style={{
            borderRadius: 12,
            padding: '40px 16px',
            fontSize: 13,
            margin: '0 28px 32px',
          }}
        >
          {status === 'all'
            ? 'No challenges yet — create one to get started.'
            : `No ${status} challenges.`}
        </div>
      ) : (
        // 28px gutter on the wrapper, NOT margin on LibTable — LibTable
        // applies w-full, so margin would make the layout 100%+56px
        // and trigger horizontal scroll (caught on the Audiobooks
        // table view; same fix here).
        <div style={{ padding: '0 28px 32px' }}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <LibTable
              fixed
              className="border border-sand"
              style={{ borderRadius: 12, overflow: 'hidden' }}
            >
              <colgroup>
                {/* order: drag + num */}
                <col style={{ width: 80 }} />
                {/* status edge */}
                <col style={{ width: 22 }} />
                {/* cover thumb */}
                <col style={{ width: 76 }} />
                {/* title (flex) */}
                <col />
                {/* days */}
                <col style={{ width: 90 }} />
                {/* sessions */}
                <col style={{ width: 100 }} />
                {/* joins (wider for comma-formatted) */}
                <col style={{ width: 110 }} />
                {/* actions */}
                <col style={{ width: 60 }} />
              </colgroup>
              <LibTable.Head>
                <tr>
                  <LibTable.Th>Order</LibTable.Th>
                  <LibTable.Th aria-label="Status" style={{ padding: 0 }}> </LibTable.Th>
                  <LibTable.Th aria-label="Cover"> </LibTable.Th>
                  <LibTable.Th>Challenge</LibTable.Th>
                  <LibTable.Th>Days</LibTable.Th>
                  <LibTable.Th>Sessions</LibTable.Th>
                  <LibTable.Th>Joins</LibTable.Th>
                  <LibTable.Th aria-label="Actions"> </LibTable.Th>
                </tr>
              </LibTable.Head>
              <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                <tbody>
                  {visible.map((c, idx) => (
                    <SortableChallengeRow
                      key={c.challenge_id}
                      challenge={c}
                      position={idx + 1}
                    />
                  ))}
                </tbody>
              </SortableContext>
            </LibTable>
          </DndContext>
        </div>
      )}
    </div>
  );
}
